// src/pages/JoinPage.js

import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import { membershipApplicationService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';

// Styled components
const JoinSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const JoinGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const JoinInfo = styled.div`
  color: ${props => props.theme.text};
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    color: ${props => props.theme.primary};
  }
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul {
    margin-left: 1.5rem;
    margin-bottom: 1.5rem;
    
    li {
      margin-bottom: 0.8rem;
      position: relative;
      padding-left: 1.5rem;
      
      &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: ${props => props.theme.primary};
        font-weight: bold;
      }
    }
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const BenefitCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const BenefitIcon = styled.div`
  font-size: 2.5rem;
  color: ${props => props.theme.primary};
  margin-bottom: 1rem;
`;

const BenefitTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.8rem;
  color: ${props => props.theme.text};
`;

const BenefitDescription = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
`;

const FormContainer = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.9rem;
  margin-top: 0.3rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: ${props => props.theme.success};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const JoinPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Définition du schéma de validation
  const validationSchema = Yup.object({
    name: Yup.string().required(t('nameRequired')),
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    phone: Yup.string().required(t('phoneRequired')),
    motivation: Yup.string().required(t('motivationRequired')).min(30, t('motivationMinLength')),
    skills: Yup.string().required(t('skillsRequired')),
    experiences: Yup.string().required(t('experiencesRequired')),
    github_profile: Yup.string().url(t('invalidUrl')).nullable(),
    linkedin_profile: Yup.string().url(t('invalidUrl')).nullable(),
    portfolio: Yup.string().url(t('invalidUrl')).nullable(),
  });
  
  // Configuration du formulaire avec Formik
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      motivation: '',
      skills: '',
      experiences: '',
      github_profile: '',
      linkedin_profile: '',
      portfolio: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Préparation des données d'application
        const applicationData = {
          applicant: {
            username: values.email.split('@')[0], // Créer un nom d'utilisateur à partir de l'email
            email: values.email,
            first_name: values.name.split(' ')[0],
            last_name: values.name.split(' ').slice(1).join(' '),
            phone_number: values.phone,
          },
          motivation: values.motivation,
          skills: values.skills,
          experiences: values.experiences,
          github_profile: values.github_profile || null,
          linkedin_profile: values.linkedin_profile || null,
          portfolio: values.portfolio || null,
        };
        
        // Envoi de la demande d'adhésion
        await membershipApplicationService.create(applicationData);
        
        setSubmissionSuccess(true);
        resetForm();
        
        // Masquer le message de succès après 8 secondes
        setTimeout(() => {
          setSubmissionSuccess(false);
        }, 8000);
      } catch (error) {
        console.error("Error submitting membership application:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });
  
  // Bénéfices de l'adhésion à la communauté
  const benefits = [
    {
      icon: 'fa-users',
      title: t('networkingTitle'),
      description: t('networkingDesc'),
    },
    {
      icon: 'fa-laptop-code',
      title: t('projectsTitle'),
      description: t('projectsDesc'),
    },
    {
      icon: 'fa-graduation-cap',
      title: t('learningTitle'),
      description: t('learningDesc'),
    },
    {
      icon: 'fa-medal',
      title: t('recognitionTitle'),
      description: t('recognitionDesc'),
    },
  ];
  
  return (
    <JoinSection>
      <Container>
        <SectionTitle
          title={t('joinCommunity')}
          // Suite de src/pages/JoinPage.js
          subtitle={t('joinCommunitySubtitle')}
        />
        
        <JoinGrid>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <JoinInfo theme={theme}>
              <h2>{t('aboutCommunity')}</h2>
              <p>{t('communityDescription1')}</p>
              <p>{t('communityDescription2')}</p>
              
              <h2>{t('whyJoin')}</h2>
              <ul>
                <li>{t('whyJoinReason1')}</li>
                <li>{t('whyJoinReason2')}</li>
                <li>{t('whyJoinReason3')}</li>
                <li>{t('whyJoinReason4')}</li>
              </ul>
              
              <BenefitsGrid>
                {benefits.map((benefit, index) => (
                  <BenefitCard key={index} theme={theme}>
                    <BenefitIcon theme={theme}>
                      <i className={`fas ${benefit.icon}`}></i>
                    </BenefitIcon>
                    <BenefitTitle theme={theme}>{benefit.title}</BenefitTitle>
                    <BenefitDescription theme={theme}>{benefit.description}</BenefitDescription>
                  </BenefitCard>
                ))}
              </BenefitsGrid>
              
              <h2>{t('membershipProcess')}</h2>
              <ol>
                <li>{t('processStep1')}</li>
                <li>{t('processStep2')}</li>
                <li>{t('processStep3')}</li>
                <li>{t('processStep4')}</li>
              </ol>
            </JoinInfo>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FormContainer theme={theme}>
              {submissionSuccess && (
                <SuccessMessage
                  theme={theme}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 style={{ marginBottom: '0.5rem' }}>{t('applicationSubmitted')}</h3>
                  <p>{t('applicationSubmittedDesc')}</p>
                </SuccessMessage>
              )}
              
              <form onSubmit={formik.handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name" theme={theme}>{t('fullName')}</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <ErrorMessage theme={theme}>{formik.errors.name}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email" theme={theme}>{t('email')}</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <ErrorMessage theme={theme}>{formik.errors.email}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone" theme={theme}>{t('phone')}</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <ErrorMessage theme={theme}>{formik.errors.phone}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="motivation" theme={theme}>{t('motivation')}</Label>
                  <TextArea
                    id="motivation"
                    name="motivation"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.motivation}
                    placeholder={t('motivationPlaceholder')}
                  />
                  {formik.touched.motivation && formik.errors.motivation && (
                    <ErrorMessage theme={theme}>{formik.errors.motivation}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="skills" theme={theme}>{t('skills')}</Label>
                  <TextArea
                    id="skills"
                    name="skills"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.skills}
                    placeholder={t('skillsPlaceholder')}
                  />
                  {formik.touched.skills && formik.errors.skills && (
                    <ErrorMessage theme={theme}>{formik.errors.skills}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="experiences" theme={theme}>{t('experiences')}</Label>
                  <TextArea
                    id="experiences"
                    name="experiences"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.experiences}
                    placeholder={t('experiencesPlaceholder')}
                  />
                  {formik.touched.experiences && formik.errors.experiences && (
                    <ErrorMessage theme={theme}>{formik.errors.experiences}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="github_profile" theme={theme}>{t('githubProfile')} ({t('optional')})</Label>
                  <Input
                    type="url"
                    id="github_profile"
                    name="github_profile"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.github_profile}
                    placeholder="https://github.com/username"
                  />
                  {formik.touched.github_profile && formik.errors.github_profile && (
                    <ErrorMessage theme={theme}>{formik.errors.github_profile}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="linkedin_profile" theme={theme}>{t('linkedinProfile')} ({t('optional')})</Label>
                  <Input
                    type="url"
                    id="linkedin_profile"
                    name="linkedin_profile"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.linkedin_profile}
                    placeholder="https://linkedin.com/in/username"
                  />
                  {formik.touched.linkedin_profile && formik.errors.linkedin_profile && (
                    <ErrorMessage theme={theme}>{formik.errors.linkedin_profile}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="portfolio" theme={theme}>{t('portfolio')} ({t('optional')})</Label>
                  <Input
                    type="url"
                    id="portfolio"
                    name="portfolio"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.portfolio}
                    placeholder="https://yourportfolio.com"
                  />
                  {formik.touched.portfolio && formik.errors.portfolio && (
                    <ErrorMessage theme={theme}>{formik.errors.portfolio}</ErrorMessage>
                  )}
                </FormGroup>
                
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  style={{ width: '100%' }}
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                      {t('submitting')}
                    </>
                  ) : (
                    t('submitApplication')
                  )}
                </Button>
              </form>
            </FormContainer>
          </motion.div>
        </JoinGrid>
      </Container>
    </JoinSection>
  );
};

export default JoinPage;